// @ts-nocheck
// Vue
import { ref, watch, computed, nextTick } from "vue";

// Dependencies
import { defineStore } from "pinia";

// API
import { getDashboard, getEmissionOverTime, AnnualEmissions } from "@/api/services/Dashboard";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

// Exports
import { DashboardAsExcel } from "@/features/Dashboard/exports";

// Providers
import { useCategoryTranslations } from "@/dataProviders/CategoryTranslations";
import { useClassificationCodes } from "@/dataProviders/ClassificationCodes";
import { useIndicators } from "@/dataProviders/Indicators";

// Utils
import {
  groupBreakdownByObjectClass,
  normalizeBreakdownCode,
  type BreakdownObjectClass,
} from "@/features/Dashboard/utils/groupBreakdownByObjectClass";

// Filters
import { reconcileSelectedModels, withNewlyAddedModels } from "./selectedModelsFilter";

// GraphQL
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { GetProjectByIdDocument } from "@/graphql";

// Types
import {
  DashbboardEmissionByMaterialType,
  DashboardEmissionBudgetType,
  DashboardTransportInstallationBudgetType,
  DashboardSiteBudgetType,
  DashboardEmissionByEpdTypeType,
  DashboardEmissionByModelType,
  DashboardEmissionByStageType,
  DashboardEmissionByTypeType,
  DashboardMappingType,
  DashboardModelType,
  DashboardProjectBreakdownType,
  DashboardProjectDataType,
  DashboardRequestType,
  DashboardResponseType,
  DashboardStageType,
  StageGroupType,
  DrilldownEmissionByTypeType,
  DrilldownEmissionByMaterialType,
} from "@/features/Dashboard/types/DashboardType";
import { InputChangeType } from "@/features/Inputs";
import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

export type ProjectMaterialByModel = Record<string, SpeckleMaterial[]>;

// Why a dashboard read is happening. "drilldown" is the narrow one: a bar click on a hotspot card,
// which changes that card's rows and nothing else.
type DashboardFetchReason = "init" | "filter" | "drilldown";

export const useDashboard = defineStore("dashboardStore", () => {
  const categoryTranslations = useCategoryTranslations();
  const classificationCodes = useClassificationCodes();
  const indicators = useIndicators();

  const allStages: StageGroupType[] = [
    {
      label: "Production",
      stages: [{ label: "A1-A3 - Production", value: "A1A3" }],
    },
    {
      label: "Construction",
      stages: [
        { label: "A4 - Transport", value: "A4" },
        { label: "A5 - Installation", value: "A5" },
      ],
    },
    {
      label: "Use",
      stages: [
        { label: "B1 - Use", value: "B1" },
        { label: "B2 - Maintenance", value: "B2" },
        { label: "B3 - Repair", value: "B3" },
        { label: "B4 - Replacement", value: "B4" },
        { label: "B5 - Refurbishment", value: "B5" },
        { label: "B6 - Operational Energy Use", value: "B6" },
        { label: "B7 - Operational Water User", value: "B7" },
      ],
    },
    {
      label: "End of Life",
      stages: [
        { label: "C1 - Demolition", value: "C1" },
        { label: "C2 - Transport", value: "C2" },
        { label: "C3 - Waste processing", value: "C3" },
        { label: "C4 - Disposal", value: "C4" },
      ],
    },
    {
      label: "Beyond System Boundary",
      stages: [{ label: "D - Reuse, recovery, recycling", value: "D" }],
    },
  ];

  // Refs

  // Utility
  const dashboardInitialized = ref<boolean>(false);

  // The project whose dashboard figures are known stale — set by a write with no dashboard on screen to refresh, cleared by
  // the fetch that honours it. Keyed by project id, not a boolean: the store may hold one project while another is being saved.
  const staleProjectId = ref<string | null>(null);

  // Project data
  const projectId = ref<string | null>(null);

  // Dashboard data
  const emissionBudget = ref<DashboardEmissionBudgetType>({} as DashboardEmissionBudgetType);
  const transportInstallationBudget = ref<DashboardTransportInstallationBudgetType>();
  // Finland only: Talo 2000 "1.1" is already out of emissionBudget when this is set.
  const siteBudget = ref<DashboardSiteBudgetType>();
  const emissionByEpdType = ref<DashboardEmissionByEpdTypeType>({} as DashboardEmissionByEpdTypeType);
  const emissionByMaterial = ref<DashbboardEmissionByMaterialType>({} as DashbboardEmissionByMaterialType);
  const emissionByModel = ref<DashboardEmissionByModelType>({} as DashboardEmissionByModelType);
  const emissionByStage = ref<DashboardEmissionByStageType>({} as DashboardEmissionByStageType);
  const emissionByType = ref<DashboardEmissionByTypeType>({} as DashboardEmissionByTypeType);
  const emissionUnit = ref<string>("");
  const mapping = ref<DashboardMappingType>({} as DashboardMappingType);
  const projectBreakdown = ref<DashboardProjectBreakdownType>({} as DashboardProjectBreakdownType);
  const projectData = ref<DashboardProjectDataType>({} as DashboardProjectDataType);

  // Project breakdown with no model/stage filter applied — surfaces answering "what does this material contribute to the
  // project" read this; the dashboard's own hotspot chart and Project Breakdown table keep reading the filtered `projectBreakdown`.
  const unfilteredProjectBreakdown = ref<DashboardProjectBreakdownType>({} as DashboardProjectBreakdownType);
  const emissionOverTime = ref<AnnualEmissions[]>([]);

  // `projectData` with the address corrected to the pinned revision's own location (base info is NOT as-of). `projectData`
  // itself stays unpatched — the Excel export reads it directly; falls back to the live address if the revision has none.
  const projectDataForDisplay = computed<DashboardProjectDataType>(() => {
    const data = projectData.value;
    const revisionLocation = projectId.value ? useProjectRevisionsStore().readLocationFor(projectId.value) : null;
    if (!revisionLocation || !data.address) return data;

    return {
      ...data,
      address: {
        ...data.address,
        street: revisionLocation.street ?? data.address.street,
        streetNumber: revisionLocation.streetNumber ?? data.address.streetNumber,
        postalCode: revisionLocation.postalCode ?? data.address.postalCode,
        city: revisionLocation.city ?? data.address.city,
        country: revisionLocation.country ?? data.address.country,
      },
    };
  });

  // Filter data
  const models = ref<DashboardModelType[]>([]);
  const selectedModels = ref<DashboardModelType[]>([]);
  // The working state's model list as of the last read, persisted, so a model added since can be told apart.
  const knownModels = ref<DashboardModelType[]>([]);
  const selectedModel = ref<DashboardModelType | null>(null);
  const stages = ref<StageGroupType[]>([]);
  const selectedStages = ref<DashboardStageType[]>([]);
  const toggledStageGroup = ref<string | null>(null);
  const selectedIndicatorTypeMapping = ref<string | null>(null);
  const requestData = ref<DashboardRequestType>({});

  // Project default indicator, sourced from Hasura (GetProjectById.thresholdIndicator).
  // Replaces the REST `data.unit` response as the source of the default selection + displayed unit.
  const defaultIndicatorTypeMapping = ref<string | null>(null);
  const defaultEmissionUnit = ref<string>("");
  const projectCalculationTypeId = ref<number | null>(null);

  // Drilldown data
  const drilldownEmissionByType = ref<DrilldownEmissionByTypeType>({
    id: "",
    name: "",
  });
  const drilldownEmissionByMaterial = ref<DrilldownEmissionByMaterialType>({
    id: "",
    name: "",
    tag: "",
    tagName: "",
  });

  // Utility
  const projectBreakdownSearch = ref<string>("");

  // Loading / error
  const fetchDashboardLoading = ref<boolean>(false);
  const emissionByMaterialLoading = ref<boolean>(false);
  const emissionByTypeLoading = ref<boolean>(false);
  const fetchDashboardError = ref<string>("");
  const error = ref<boolean>(false);
  const isInitialLoad = ref<boolean>(true);
  // True when a non-empty model selection was restored from localStorage on this load, so the
  // initial-load seed-all below must not overwrite the user's deliberately-chosen subset.
  const modelsRestoredFromStorage = ref<boolean>(false);
  const isFilterChange = ref<boolean>(false);

  // Monotonic token so a superseded (out-of-order) fetch can't overwrite a newer one's data
  let fetchSeq = 0;

  // Same out-of-order guard for the unfiltered breakdown read, on its own counter — sharing `fetchSeq` would let the two
  // concurrent reads supersede each other and both would throw their response away.
  let unfilteredFetchSeq = 0;

  // Computed - compatibility

  // Exposes dashboard shape expected by MaterialMappingView
  const dashboard = computed(() => ({
    emissionBudget: emissionBudget.value,
    unit: { label: emissionUnit.value, id: selectedIndicatorTypeMapping.value },
    mapping: mapping.value,
  }));

  const dashboardIsLoading = computed(() => fetchDashboardLoading.value);

  const mappingPercentage = computed(() => emissionBudget.value?.used?.percentage ?? 0);

  // True when models/stages/indicator are all at their defaults ("All" + the project default
  // indicator). Used to disable the reset-filters button when there is nothing to reset.
  const isAtDefaultFilters = computed(() => {
    const allModelsCount = (models.value ?? []).length;
    const selectedModelsCount = (selectedModels.value ?? []).length;
    const modelsAtDefault = selectedModelsCount === 0 || selectedModelsCount === allModelsCount;

    const totalStages = (stages.value ?? []).reduce((acc, group) => acc + group.stages.length, 0);
    const selectedStagesCount = (selectedStages.value ?? []).length;
    const stagesAtDefault = selectedStagesCount === 0 || selectedStagesCount === totalStages;

    const indicatorAtDefault =
      selectedIndicatorTypeMapping.value === null || selectedIndicatorTypeMapping.value === defaultIndicatorTypeMapping.value;

    return modelsAtDefault && stagesAtDefault && indicatorAtDefault;
  });

  // Computed - project breakdown

  // Classification code -> its object class. Empty for systems without object classes, and until
  // the ClassificationCodes provider has loaded (it is mounted by pages/Dashboard.vue).
  const objectClassByCode = computed(() => {
    const index = new Map<string, BreakdownObjectClass>();
    for (const code of classificationCodes.getClassificationCodes()) {
      if (!code.objectClass) continue;
      index.set(normalizeBreakdownCode(code.code), { code: code.objectClass.code, name: code.objectClass.name });
    }
    return index;
  });

  // The breakdown with the object class inserted as its outermost level. Falls back to the raw tree
  // whenever the object class level does not apply, so every consumer can read this unconditionally.
  const projectBreakdownGrouped = computed<DashboardProjectBreakdownType>(() =>
    groupBreakdownByObjectClass(projectBreakdown.value, objectClassByCode.value)
  );

  // Setters

  const setDashboardInitialized = (value: boolean) => {
    dashboardInitialized.value = value;
  };

  const setProjectId = (id: string) => {
    projectId.value = id;
  };

  // Marks a project's figures stale for a write with nowhere on screen to refresh them; the next read fetches instead of
  // reusing the last load. A mark rather than an immediate fetch — a settings save is followed about as often by another edit as by a dashboard visit.
  const invalidateDashboard = (id: string) => {
    if (id) staleProjectId.value = String(id);
  };

  const setRequestData = (data: DashboardRequestType) => {
    requestData.value = data;
  };

  function setProjectBreakdownSearch(event: Event) {
    projectBreakdownSearch.value = (event.target as HTMLInputElement).value;
  }

  // Setters - drilldown

  function setDrilldownEmissionByType({ id, name }: DrilldownEmissionByTypeType) {
    drilldownEmissionByType.value = { id, name };
  }

  function setDrilldownEmissionByMaterial({ id, name, tag, tagName }: DrilldownEmissionByMaterialType) {
    drilldownEmissionByMaterial.value = { id, name, tag, tagName };
  }

  // Methods - Filter

  const setSelectedModels = (models: DashboardModelType[]) => {
    selectedModels.value = models;
  };

  const setKnownModels = (names: DashboardModelType[]) => {
    knownModels.value = [...names];
    localStorage.setItem(`gaiup-${projectId.value}-dashboard-knownModels`, JSON.stringify(knownModels.value));
  };

  const setSelectedStages = (newStages: DashboardStageType[]) => {
    selectedStages.value = newStages;
  };

  function toggleModel(models: InputChangeType[]) {
    const index = selectedModels.value.indexOf(models.name);
    if (index === -1) {
      selectedModels.value.push(models.name);
    } else {
      selectedModels.value.splice(index, 1);
    }
  }

  function toggleAllModels() {
    if ((selectedModels.value?.length ?? 0) === (models.value?.length ?? 0)) {
      selectedModels.value = [];
      return;
    }
    selectedModels.value = [...models.value];
  }

  function toggleStage(stage: InputChangeType) {
    const index = selectedStages.value.indexOf(stage.name);
    if (index === -1) {
      selectedStages.value.push(stage.name);
    } else {
      selectedStages.value.splice(index, 1);
    }
  }

  function toggleStageGroup(stageGroup: string) {
    const stageGroupStages = stages.value.find((stage) => stage.label === stageGroup)?.stages;

    if (!stageGroupStages) return;

    const selectedStagesInGroup = stageGroupStages.filter((stage) => selectedStages.value.includes(stage.value));

    if (selectedStagesInGroup.length !== stageGroupStages.length) {
      stageGroupStages.forEach((stage) => {
        if (!selectedStages.value.includes(stage.value)) {
          selectedStages.value.push(stage.value);
        }
      });
      return;
    }

    stageGroupStages.forEach((stage) => {
      const index = selectedStages.value.indexOf(stage.value);
      selectedStages.value.splice(index, 1);
    });
  }

  function toggleAllStages() {
    if ((selectedStages.value?.length ?? 0) === (stages.value ?? []).reduce((acc, x) => acc + x.stages.length, 0)) {
      selectedStages.value = [];
      return;
    }

    selectedStages.value = stages.value.reduce((acc, stageGroup) => {
      return [...acc, ...stageGroup.stages.map((stage) => stage.value)];
    }, [] as string[]);
  }

  function toggleStageGroupAccordion(stageGroup: string) {
    if (toggledStageGroup.value === stageGroup) {
      toggledStageGroup.value = null;
      return;
    }
    toggledStageGroup.value = stageGroup;
  }

  function toggleSelectedIndicatorTypeMapping(indicatorTypeMapping: string) {
    selectedIndicatorTypeMapping.value = indicatorTypeMapping;
  }

  // Reset all dashboard filters to their defaults: all models, all stages, and the project's default
  // indicator/unit (Hasura threshold). The filter watcher (:565) drives a single refetch.
  function resetFilters() {
    selectedModels.value = [...(models.value ?? [])];
    selectedStages.value = [];
    selectedIndicatorTypeMapping.value = defaultIndicatorTypeMapping.value;
  }

  // Methods - Data fetching and mapping

  async function initDashboard(projectIdParam: string) {
    if (!projectIdParam) return;

    // Reset initial load flag when switching projects
    if (projectId.value !== projectIdParam) {
      isInitialLoad.value = true;
    }

    // Reset error
    fetchDashboardError.value = "";
    error.value = false;

    // Reset drilldown
    drilldownEmissionByType.value = { id: "", name: "" };
    drilldownEmissionByMaterial.value = {
      id: "",
      name: "",
      tag: "",
      tagName: "",
    };

    // Set project id
    setProjectId(projectIdParam);

    // Load the project's default indicator from Hasura before reading localStorage, so the default
    // selection + displayed unit come from the project (not the REST `data.unit` response).
    await loadProjectDefaults();

    const indicatorBeforeStorage = selectedIndicatorTypeMapping.value;

    // Load localStorage data into selectedModels (triggers watcher -> fetchDataAndMapDashboard)
    await loadLocalStorageData();

    // Unfiltered breakdown read fired (not awaited) so the dashboard's own data doesn't wait on it. Skipped when
    // localStorage moved the indicator — the indicator watcher below covers this init, avoiding a duplicate request.
    if (selectedIndicatorTypeMapping.value === indicatorBeforeStorage) void getUnfilteredProjectBreakdown();
  }

  // Fetch the project's default indicator from Hasura: `thresholdIndicator` first, else the calc type's default mapping
  // (mirrors Filter.vue's effectiveMappingId). Replaces the REST `data.unit` echo.
  async function loadProjectDefaults() {
    if (!projectId.value) return;
    try {
      const { data } = await gaiupApolloClient.query({
        query: GetProjectByIdDocument,
        variables: { id: projectId.value },
        fetchPolicy: "network-only",
      });
      const calculationType = data?.project_by_pk?.calculation?.calculationType ?? null;
      const thresholdIndicator = calculationType?.thresholdIndicator ?? null;
      projectCalculationTypeId.value = calculationType?.id ?? null;

      if (thresholdIndicator) {
        defaultIndicatorTypeMapping.value = thresholdIndicator.id ?? null;
        defaultEmissionUnit.value = indicators.getIndicatorUnit(
          thresholdIndicator.indicatorType,
          thresholdIndicator.denominatorUnit ?? undefined
        );
      } else {
        // No configured threshold indicator — fall back to the calc type's default mapping. May be unresolved here if the
        // indicators store hasn't loaded yet; the seed watcher below resolves it reactively once mappings arrive.
        const fallback =
          projectCalculationTypeId.value != null
            ? indicators.getDefaultIndicatorTypeMapping(projectCalculationTypeId.value)
            : undefined;
        defaultIndicatorTypeMapping.value = fallback?.id ?? null;
        defaultEmissionUnit.value = fallback
          ? indicators.getIndicatorUnit(fallback.indicatorType, fallback.denominatorUnit ?? undefined)
          : "";
      }
    } catch (err) {
      // Best-effort: the default indicator/unit is non-critical. The selection still falls back to
      // localStorage, and the unit watcher resolves it from the indicators store once mappings load.
      console.error("Failed to load project default indicator:", err);
      defaultIndicatorTypeMapping.value = null;
      defaultEmissionUnit.value = "";
      projectCalculationTypeId.value = null;
    }
  }

  function loadLocalStorageData() {
    const selectedModelsLocalStorage = localStorage.getItem(`gaiup-${projectId.value}-dashboard-selectedModels`);
    const knownModelsLocalStorage = localStorage.getItem(`gaiup-${projectId.value}-dashboard-knownModels`);
    const selectedStagesLocalStorage = localStorage.getItem(`gaiup-${projectId.value}-dashboard-selectedStages`);
    const selectedIndicatorTypeMappingLocalStorage = localStorage.getItem(
      `gaiup-${projectId.value}-dashboard-selectedIndicatorTypeMapping`
    );

    const safeParse = (value: string | null, defaultValue: string) => {
      if (!value || value === "undefined" || value === "null") {
        try {
          return JSON.parse(defaultValue);
        } catch {
          return defaultValue;
        }
      }
      try {
        return JSON.parse(value);
      } catch {
        try {
          return JSON.parse(defaultValue);
        } catch {
          return defaultValue;
        }
      }
    };

    const parsedModels = safeParse(selectedModelsLocalStorage, "[]");
    selectedModels.value = Array.isArray(parsedModels) ? parsedModels : [];
    modelsRestoredFromStorage.value = Array.isArray(parsedModels) && parsedModels.length > 0;

    const parsedKnownModels = safeParse(knownModelsLocalStorage, "[]");
    knownModels.value = Array.isArray(parsedKnownModels) ? parsedKnownModels : [];

    const parsedStages = safeParse(selectedStagesLocalStorage, "[]");
    selectedStages.value = Array.isArray(parsedStages) ? parsedStages : [];

    // Seed from localStorage if present, otherwise from the project's Hasura default indicator.
    const parsedIndicatorTypeMapping = safeParse(selectedIndicatorTypeMappingLocalStorage, "null");
    selectedIndicatorTypeMapping.value = parsedIndicatorTypeMapping ?? defaultIndicatorTypeMapping.value;
  }

  function constructDrillingRequestData() {
    const ddType = drilldownEmissionByType.value;
    const ddMaterial = drilldownEmissionByMaterial.value;
    return {
      emissionByMaterial: ddMaterial.tag ? { tag: ddMaterial.tag, id: ddMaterial.id } : undefined,
      emissionByType: ddType.id ? { id: ddType.id } : undefined,
    };
  }

  function constructFilterRequestData() {
    return {
      models: selectedModels.value?.length ? selectedModels.value : undefined,
      stages: selectedStages.value?.length ? selectedStages.value : undefined,
      indicatorTypeMappingId: selectedIndicatorTypeMapping.value ?? undefined,
      // Pinned to the revision being read (previewed, or the current one) so every dashboard read stays revision-correct.
      // Resolved at call time here rather than threaded as a parameter, so callers don't have to pass it.
      revisionId: useProjectRevisionsStore().readPinRevisionIdFor(projectId.value) ?? undefined,
    };
  }

  function constructRequestData(): DashboardRequestType {
    return {
      ...constructDrillingRequestData(),
      ...constructFilterRequestData(),
    };
  }

  // `reason` says what the caller changed. A drilldown changes ONE chart's rows, so it skips the
  // emissions-over-time leg and leaves every other card's data alone (see the mapData call below).
  async function fetchDataAndMapDashboard(reason: DashboardFetchReason = "filter") {
    // Claim the latest sequence number; any fetch with an older seq is stale and bails below.
    const seq = ++fetchSeq;
    fetchDashboardLoading.value = true;

    await setRequestData(constructRequestData());

    // Its payload is constructFilterRequestData() alone, so a drilldown cannot change its answer.
    // Asking anyway cost a second request per bar click, 3.5 to 5.8s of it on dev.
    const emissionOverTimePromise =
      reason === "drilldown" ? null : getEmissionOverTime(projectId.value, constructFilterRequestData());
    const data = await getDashboard(projectId.value, requestData.value);

    // Superseded by a newer fetch — drop this response without touching shared state
    if (seq !== fetchSeq) return;

    // post() resolves undefined on a network failure; without this the loading flag stayed true for good.
    if (!data) {
      fetchDashboardError.value = "Could not reach the server to load the dashboard.";
      fetchDashboardLoading.value = false;
      return;
    }

    // Auto-select all models on initial load only. Reassigning selectedModels re-triggers the filter watcher (single refetch);
    // skipped when a non-empty selection was restored from localStorage, so it survives a reload instead of being back-filled to "all".
    if (isInitialLoad.value && !modelsRestoredFromStorage.value) {
      const unselectedModels = (data.models ?? []).filter((model) => !(selectedModels.value ?? []).includes(model));
      if (unselectedModels.length > 0) {
        selectedModels.value = [...selectedModels.value, ...unselectedModels];
        return;
      }
    }

    // Check for project id
    if (!projectId.value) {
      fetchDashboardLoading.value = false;
      return;
    }

    // Handle errors
    if (data.error) {
      fetchDashboardError.value = data.data;
      fetchDashboardLoading.value = false;
      return;
    }

    // Prunes removed models from the selection and adds new ones when all were selected; returning after reassignment keeps
    // mapData off a mismatched pair. Working state only — reconciling against a preview would persist its models to localStorage.
    const isReadingFrozenRevision = useProjectRevisionsStore().readPinRevisionIdFor(projectId.value) != null;
    if (!isReadingFrozenRevision) {
      const withAddedModels = withNewlyAddedModels(knownModels.value, data.models, selectedModels.value);
      const reconciledModels = reconcileSelectedModels(data.models, withAddedModels);
      const selectionChanged =
        reconciledModels.length !== (selectedModels.value ?? []).length ||
        reconciledModels.some((model, index) => model !== selectedModels.value[index]);
      if (selectionChanged) {
        selectedModels.value = reconciledModels;
        return;
      }
      if (data.models?.length) setKnownModels(data.models);
    }

    // Validate selected stages against data stages.
    // Reassigning selectedStages re-triggers the filter watcher, which drives the single refetch.
    const selectedStagesInData = (selectedStages.value ?? []).filter((stage) => (data.stages ?? []).includes(stage));
    if (selectedStagesInData.length !== selectedStages.value.length) {
      selectedStages.value = selectedStagesInData;
      return;
    }

    fetchDashboardError.value = "";
    await mapData(data, reason);

    if (emissionOverTimePromise) {
      try {
        const emissionOverTimeData = await emissionOverTimePromise;
        if (seq === fetchSeq) emissionOverTime.value = emissionOverTimeData?.items || [];
      } catch (err) {
        console.error("Failed to fetch emission over time data:", err);
        if (seq === fetchSeq) emissionOverTime.value = [];
      }
    }

    // Superseded while finishing — let the newer fetch own the loading flag
    if (seq !== fetchSeq) return;

    fetchDashboardLoading.value = false;
    isInitialLoad.value = false;
  }

  // Second dashboard read feeding `unfilteredProjectBreakdown`. `models`/`stages` are omitted deliberately — that's what
  // makes it unfiltered; `indicatorTypeMappingId`/`revisionId` are kept so the figure follows the selected indicator and stays revision-pinned.
  async function getUnfilteredProjectBreakdown() {
    const id = projectId.value;
    if (!id) return;

    const seq = ++unfilteredFetchSeq;

    try {
      const data = await getDashboard(id, {
        indicatorTypeMappingId: selectedIndicatorTypeMapping.value ?? undefined,
        revisionId: useProjectRevisionsStore().readPinRevisionIdFor(id) ?? undefined,
      });

      // Superseded by a newer read — drop this response without touching shared state
      if (seq !== unfilteredFetchSeq) return;

      // Keep the last good breakdown on an error response rather than blanking the modal's figures
      if (!data || data.error) return;

      unfilteredProjectBreakdown.value = data.projectBreakdown;
    } catch (err) {
      // Best-effort: this read only feeds the mapping modal's project-level figures. The dashboard itself is driven by
      // fetchDataAndMapDashboard and is unaffected — the modal falls back rather than presenting EPD numbers as project ones.
      console.error("Failed to fetch the unfiltered project breakdown:", err);
    }
  }

  function translateEmissionByMaterial(emissionByMaterial: DashbboardEmissionByMaterialType) {
    const key = "materials";

    if (emissionByMaterial[key]) {
      const entries = emissionByMaterial[key].entries.map((item) => ({
        ...item,
        label: categoryTranslations.translate(item.label).value,
      }));

      emissionByMaterial[key].entries = entries;
    }

    return emissionByMaterial;
  }

  async function mapData(data: DashboardResponseType, reason: DashboardFetchReason = "filter") {
    // A drilldown only changes `emissionByType`/`emissionByMaterial` — everything else in the response comes back identical.
    // Reassigning the rest anyway made every other card on the dashboard redraw for a single bar click, so only these two are set here.
    if (reason === "drilldown") {
      emissionByType.value = data.emissionByType;
      emissionByMaterial.value = translateEmissionByMaterial(data.emissionByMaterial);
      return;
    }

    // emissionUnit and the default indicator mapping are sourced from Hasura (see the emissionUnit
    // watcher and loadProjectDefaults). The REST `data.unit` field is intentionally ignored.
    emissionBudget.value = data.emissionBudget;
    transportInstallationBudget.value = data.transportInstallationBudget;
    siteBudget.value = data.siteBudget;
    emissionByType.value = data.emissionByType;
    emissionByEpdType.value = data.emissionByEpdType;
    emissionByModel.value = data.emissionByModel;
    emissionByStage.value = data.emissionByStage;
    mapping.value = data.mapping;
    emissionByMaterial.value = translateEmissionByMaterial(data.emissionByMaterial);
    projectBreakdown.value = data.projectBreakdown;
    projectData.value = data.projectData;

    // Set models
    models.value = data.models ?? [];

    // Map stages from data using allStages as a template
    stages.value = allStages
      .map((stageGroup) => {
        const stagesInGroup = stageGroup.stages.filter((stage) => (data.stages ?? []).includes(stage.value));
        if (stagesInGroup.length === 0) return null;
        return {
          label: stageGroup.label,
          stages: stagesInGroup,
        };
      })
      .filter((x) => x !== null) as StageGroupType[];
  }

  async function refreshDashboard() {
    // No project loaded in this store — e.g. writes made during project creation, before any
    // dashboard exists. Fetching would hit api/dashboard/null and 400.
    if (!projectId.value) return;

    // Fired alongside, not awaited — see initDashboard. Every write that refreshes the dashboard
    // (a material saved, a mapping removed, a revision switched) must move the modal's figure too.
    void getUnfilteredProjectBreakdown();

    await fetchDataAndMapDashboard();
  }

  // In-flight fetch so concurrent callers (the layout's fetch and a page's own self-fetch, e.g. Breakdown/Materials/Waste/PDF
  // report mounting the same tick) share one load instead of racing two. Entry dies once the fetch settles, so a later forced refresh always issues its own request.
  let inflightDashboardFetch: { id: string; promise: Promise<void> } | null = null;

  // fetchDashboard maintains backward compat with MaterialMapping components
  async function fetchDashboard(projectIdParam?: string, force = false) {
    const id = projectIdParam ?? projectId.value;
    if (!id) return;

    // Read staleProjectId here so an unforced arrival on a stale store fetches instead of no-oping below. Not cleared here —
    // only by the fetch that actually goes out — so a caller that joins an already in-flight request leaves the mark standing for the next arrival.
    const stale = staleProjectId.value === String(id);

    if (!force && !stale && dashboardInitialized.value && projectId.value === String(id)) return;

    // Join a fetch already running for this project, forced callers included — a request that started this tick IS current,
    // and this guards precisely the case of one forced and one unforced caller racing.
    if (inflightDashboardFetch?.id === String(id)) return inflightDashboardFetch.promise;

    if (stale) staleProjectId.value = null;

    const fetchPromise = (async () => {
      if (!projectId.value || projectId.value !== String(id)) {
        await initDashboard(String(id));
      } else {
        // Reload defaults only on the stale path: a settings save can change the calculation type (and its threshold
        // indicator), which `refreshDashboard` alone wouldn't pick up. Every other forced refetch here is a revision pin change, which can't alter them.
        if (stale) await loadProjectDefaults();
        await refreshDashboard();
      }
    })();

    inflightDashboardFetch = { id: String(id), promise: fetchPromise };
    try {
      await fetchPromise;
    } finally {
      inflightDashboardFetch = null;
    }
  }

  // Methods - Exporting

  function exportDashboardAsExcel() {
    return DashboardAsExcel({
      emissionBudget: emissionBudget.value,
      emissionByEpdType: emissionByEpdType.value,
      emissionByMaterial: emissionByMaterial.value,
      emissionByModel: emissionByModel.value,
      emissionByStage: emissionByStage.value,
      emissionByType: emissionByType.value,
      emissionUnit: emissionUnit.value,
      mapping: mapping.value,
      // Grouped, so the exported sheet matches the hierarchy shown on screen.
      projectBreakdown: projectBreakdownGrouped.value,
      projectData: projectData.value,
      emissionOverTime: emissionOverTime.value,
      drilldownEmissionByMaterial: drilldownEmissionByMaterial.value,
      drilldownEmissionByType: drilldownEmissionByType.value,
      projectId: projectId.value,
    });
  }

  // Methods - Utility

  function getStageLabel(stageValue: string) {
    const stage = stages.value.find((stageGroup) => stageGroup.stages.some((stage) => stage.value === stageValue));
    return stage?.stages.find((stage) => stage.value === stageValue)?.label || "";
  }

  function getIsSomethingMapped() {
    return mapping.value?.material?.percentage > 0 || false;
  }

  function getLoading() {
    return fetchDashboardLoading.value && (isInitialLoad.value || isFilterChange.value);
  }

  function getError() {
    return error.value;
  }

  // Watchers

  watch(
    selectedModels,
    (newValue) => {
      localStorage.setItem(`gaiup-${projectId.value}-dashboard-selectedModels`, JSON.stringify(newValue));
    },
    { deep: true }
  );

  watch(
    selectedStages,
    (newValue) => {
      localStorage.setItem(`gaiup-${projectId.value}-dashboard-selectedStages`, JSON.stringify(newValue));
    },
    { deep: true }
  );

  watch(
    selectedIndicatorTypeMapping,
    (newValue) => {
      if (newValue) {
        localStorage.setItem(`gaiup-${projectId.value}-dashboard-selectedIndicatorTypeMapping`, JSON.stringify(newValue));
      }
    },
    { deep: true }
  );

  // Derives the displayed unit from the selected indicator mapping (Hasura), same source of truth as the filter chips
  // (Filter.vue / Denominators.vue). Falls back to the project threshold unit until the indicators store has loaded mappings.
  watch(
    [selectedIndicatorTypeMapping, () => indicators.getIndicatorTypeMappings()],
    () => {
      const mappingId = selectedIndicatorTypeMapping.value;
      const mapping = mappingId ? indicators.getIndicatorTypeMappings().find((m) => m.id === mappingId) : undefined;
      emissionUnit.value = mapping
        ? indicators.getIndicatorUnit(mapping.indicatorType, mapping.denominatorUnit ?? undefined)
        : defaultEmissionUnit.value;
    },
    { immediate: true }
  );

  // Seeds the default indicator once the indicators store has loaded, only if nothing is selected yet — covers projects
  // whose calc-type default mapping only becomes available after the mappings arrive (unresolvable at init time).
  watch(
    () => indicators.getIndicatorTypeMappings(),
    () => {
      if (selectedIndicatorTypeMapping.value || projectCalculationTypeId.value == null) return;
      const seedId =
        defaultIndicatorTypeMapping.value ??
        indicators.getDefaultIndicatorTypeMapping(projectCalculationTypeId.value)?.id ??
        null;
      if (seedId) selectedIndicatorTypeMapping.value = seedId;
    }
  );

  // The indicator leg only. The unfiltered read exists precisely to ignore the model/stage filters,
  // so refetching it when those change would be a full dashboard request for an identical response.
  watch(selectedIndicatorTypeMapping, () => {
    void getUnfilteredProjectBreakdown();
  });

  watch(
    [selectedModels, selectedStages, selectedIndicatorTypeMapping],
    async () => {
      isFilterChange.value = true;
      await nextTick();
      await fetchDataAndMapDashboard();
      setDashboardInitialized(true);
    },
    { deep: true }
  );

  watch(
    [drilldownEmissionByType, drilldownEmissionByMaterial],
    async () => {
      await nextTick();
      await fetchDataAndMapDashboard("drilldown");
      setDashboardInitialized(true);
    },
    { deep: true }
  );

  watch([fetchDashboardError], ([newFetchDashboardError]) => {
    if (newFetchDashboardError) console.error({ newFetchDashboardError });
    error.value = !!newFetchDashboardError;
  });

  watch(
    drilldownEmissionByMaterial,
    () => {
      emissionByMaterialLoading.value = true;
    },
    { deep: true }
  );

  watch(
    drilldownEmissionByType,
    () => {
      emissionByTypeLoading.value = true;
    },
    { deep: true }
  );

  watch(fetchDashboardLoading, (newValue) => {
    if (!newValue) {
      emissionByMaterialLoading.value = false;
      emissionByTypeLoading.value = false;
      isFilterChange.value = false;
    }
  });

  // Return

  return {
    // Refs - project
    projectId,
    selectedModel,

    // Refs - dashboard data
    emissionBudget,
    transportInstallationBudget,
    siteBudget,
    emissionByEpdType,
    emissionByMaterial,
    emissionByModel,
    emissionByStage,
    emissionByType,
    emissionUnit,
    mapping,
    projectBreakdown,
    projectBreakdownGrouped,
    unfilteredProjectBreakdown,
    projectData,
    projectDataForDisplay,
    emissionOverTime,

    // Refs - filter
    models,
    selectedModels,
    stages,
    selectedStages,
    toggledStageGroup,
    selectedIndicatorTypeMapping,
    defaultIndicatorTypeMapping,

    // Refs - drilldown
    drilldownEmissionByType,
    drilldownEmissionByMaterial,

    // Refs - utility
    projectBreakdownSearch,

    // Setters
    setProjectBreakdownSearch,
    setSelectedModels,
    setSelectedStages,

    // Setters - drilldown
    setDrilldownEmissionByType,
    setDrilldownEmissionByMaterial,

    // Methods - filter
    toggleAllModels,
    toggleModel,
    toggleStage,
    toggleStageGroup,
    toggleAllStages,
    toggleStageGroupAccordion,
    toggleSelectedIndicatorTypeMapping,
    resetFilters,

    // Methods - data fetching
    initDashboard,
    refreshDashboard,
    fetchDashboard,
    invalidateDashboard,

    // Methods - exporting
    exportDashboardAsExcel,

    // Methods - utility
    getStageLabel,
    getIsSomethingMapped,
    getLoading,
    getError,

    // Loading / error
    fetchDashboardLoading,
    isInitialLoad,
    emissionByMaterialLoading,
    emissionByTypeLoading,

    // Compatibility (MaterialMappingView, dashboard.store consumers)
    dashboard,
    dashboardIsLoading,
    mappingPercentage,
    isAtDefaultFilters,
  };
});
